// src/components/billing/CardUpdateForm.js — inline Payment Element form for
// swapping the default card on /settings/billing. Fetches its own SetupIntent;
// the parent verifies the confirmed intent server-side via onVerified.
import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripePromise } from '../../lib/stripePromise';

const API = process.env.REACT_APP_API_URL;

const SetupForm = ({ onVerified, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        setFormError(null);
        const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: { return_url: `${window.location.origin}/settings/billing?setup=1` },
            redirect: 'if_required',
        });
        if (error) {
            setFormError(error.message || 'Card update failed. Please try again.');
            setSubmitting(false);
            return;
        }
        await onVerified(setupIntent.id);
        setSubmitting(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <PaymentElement />
            {formError && <div className="text-red-400 text-sm">{formError}</div>}
            <div className="flex gap-2">
                <button type="submit" disabled={!stripe || submitting}
                    className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2 px-5 rounded-lg disabled:opacity-50 transition-colors">
                    {submitting ? 'Saving…' : 'Save card'}
                </button>
                <button type="button" onClick={onCancel} disabled={submitting}
                    className="text-sm font-medium text-neutral-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-4 rounded-lg transition-colors">
                    Keep current card
                </button>
            </div>
        </form>
    );
};

const CardUpdateForm = ({ onVerified, onCancel }) => {
    const [clientSecret, setClientSecret] = useState(null);
    const [setupError, setSetupError] = useState(null);

    useEffect(() => {
        // No publishable key → the config-error render below; don't mint a
        // SetupIntent the client can never confirm.
        if (!process.env.REACT_APP_STRIPE_PK) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API}/api/create_card_setup.php`, {
                    method: 'POST', credentials: 'include', headers: { 'Accept': 'application/json' },
                });
                const data = await res.json();
                if (cancelled) return;
                if (res.ok && data.status === 'success' && data.client_secret) {
                    setClientSecret(data.client_secret);
                } else {
                    setSetupError(data.message || 'Could not start the card update. Please try again.');
                }
            } catch (e) {
                if (!cancelled) setSetupError('Could not start the card update. Please try again.');
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!process.env.REACT_APP_STRIPE_PK) {
        return <p className="text-sm text-neutral-400">Payments are not configured. Please contact support.</p>;
    }
    if (setupError) {
        return <p className="text-sm text-red-400">{setupError}</p>;
    }
    if (!clientSecret) {
        return (
            <div className="flex items-center justify-center h-24">
                <div className="w-6 h-6 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    return (
        <Elements key={clientSecret} stripe={getStripePromise()} options={{
            clientSecret,
            appearance: {
                theme: 'night',
                variables: { colorPrimary: '#06b6d4', colorBackground: '#0b0e14', colorText: '#e5e5e5', borderRadius: '8px' },
            },
        }}>
            <SetupForm onVerified={onVerified} onCancel={onCancel} />
        </Elements>
    );
};

export default CardUpdateForm;
